import type { KeyboardEvent, ReactNode } from "react";

import { cn, hasRenderableNode } from "../base/utils";
import { WorkbenchCollection } from "./WorkbenchCollection";

export interface WorkbenchAssetGalleryItem {
  id: string;
  url: string;
  alt: string;
  title?: ReactNode;
  description?: ReactNode;
  badges?: ReactNode[];
}

export interface WorkbenchAssetGalleryProps {
  items: WorkbenchAssetGalleryItem[];
  selectedItemId?: string;
  onItemSelect?: (item: WorkbenchAssetGalleryItem) => void;
  emptyState?: ReactNode;
  ariaLabel?: string;
  className?: string;
}

const activateItem = (
  event: KeyboardEvent<HTMLButtonElement>,
  item: WorkbenchAssetGalleryItem,
  onItemSelect?: (item: WorkbenchAssetGalleryItem) => void,
) => {
  if (!onItemSelect || (event.key !== "Enter" && event.key !== " ")) return;
  event.preventDefault();
  onItemSelect(item);
};

export function WorkbenchAssetGallery({
  items,
  selectedItemId,
  onItemSelect,
  emptyState = null,
  ariaLabel = "Assets",
  className,
}: WorkbenchAssetGalleryProps) {
  if (!items.length) {
    return hasRenderableNode(emptyState) ? <>{emptyState}</> : null;
  }

  return (
    <div data-monkeys-component="workbench-asset-gallery">
      <WorkbenchCollection<WorkbenchAssetGalleryItem>
        items={items}
        config={{
          getItemKey: (item) => item.id,
          layout: {
            mode: "grid",
            columns: "auto",
            minCardWidth: "12rem",
            idealCardWidth: "16rem",
            gap: 12,
          },
          appearance: { hover: "none" },
        }}
        ariaLabel={ariaLabel}
        className={className}
        renderItem={(item) => {
          const selected = selectedItemId === item.id;
          return (
            <button
              type="button"
              aria-pressed={onItemSelect ? selected : undefined}
              onClick={onItemSelect ? () => onItemSelect(item) : undefined}
              onKeyDown={(event) => activateItem(event, item, onItemSelect)}
              className={cn(
                "group w-full overflow-hidden rounded-lg border bg-card text-left text-card-foreground outline-none transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none",
                selected
                  ? "border-primary ring-1 ring-primary"
                  : "border-border/70 hover:border-primary/40",
                !onItemSelect && "cursor-default",
              )}
            >
              <span className="block aspect-[4/3] overflow-hidden bg-muted">
                <img
                  src={item.url}
                  alt={item.alt}
                  loading="lazy"
                  className="size-full object-cover transition-transform duration-200 group-hover:scale-[1.02] motion-reduce:transition-none"
                />
              </span>
              <span className="block p-3">
                {hasRenderableNode(item.title) ? (
                  <span className="block truncate text-sm font-semibold text-foreground">
                    {item.title}
                  </span>
                ) : null}
                {hasRenderableNode(item.description) ? (
                  <span className="mt-1 block text-xs leading-5 text-muted-foreground">
                    {item.description}
                  </span>
                ) : null}
                {item.badges?.length ? (
                  <span className="mt-2 flex flex-wrap gap-1.5">
                    {item.badges.map((badge, index) => (
                      <span
                        key={`${item.id}:badge:${index}`}
                        className="rounded-full bg-secondary px-2 py-0.5 text-[0.625rem] text-secondary-foreground"
                      >
                        {badge}
                      </span>
                    ))}
                  </span>
                ) : null}
              </span>
            </button>
          );
        }}
      />
    </div>
  );
}
